/**
 * Caregiver ClientSchedulePage (CONTRACTS §6, spec §9.3): create,
 * edit-future and deactivate medication schedules for a linked client.
 * Past intake history is never edited here — only schedules.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BottomSheet,
  Button,
  Card,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusChip,
  useToast,
} from '@/components/ui'
import { ChevronLeftIcon, PillIcon, PlusIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import { useAuth } from '@/features/auth'
import type { MedicationSchedule } from '@/types'
import { errorMessage, useMedicationSchedules } from '@/features/medication'
import { ScheduleForm } from './ScheduleForm'
import {
  emptyScheduleValues,
  recurrenceRuleFromValues,
  valuesFromSchedule,
  valuesToNewSchedule,
  type ScheduleFormValues,
} from './scheduleFormLogic'
import './caregiver.css'

function describeSchedule(schedule: MedicationSchedule): string {
  const parts: string[] = []
  parts.push(schedule.dosage || 'geen dosering ingevuld')
  parts.push(`${schedule.quantity}×`)
  parts.push(`om ${schedule.localTime}`)
  const recurrence =
    schedule.recurrenceType === 'once'
      ? `eenmalig op ${schedule.startDate}`
      : schedule.recurrenceType === 'daily'
        ? 'dagelijks'
        : schedule.recurrenceType === 'weekly'
          ? `wekelijks (${schedule.recurrenceRule.weekdays?.length ?? 0} dagen)`
          : `maandelijks (dag ${schedule.recurrenceRule.dayOfMonth ?? '?'})`
  parts.push(recurrence)
  if (schedule.endDate) parts.push(`tot ${schedule.endDate}`)
  return parts.join(' · ')
}

export default function ClientSchedulePage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { userId } = useAuth()
  const { status, schedules, refresh } = useMedicationSchedules(clientId ?? null)

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<MedicationSchedule | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<MedicationSchedule | null>(null)
  const [saving, setSaving] = useState(false)

  const activeSchedules = useMemo(() => schedules.filter((schedule) => schedule.active), [schedules])
  const inactiveSchedules = useMemo(() => schedules.filter((schedule) => !schedule.active), [schedules])

  const create = async (values: ScheduleFormValues) => {
    if (!clientId || !userId || saving) return
    setSaving(true)
    try {
      await repositories.medication.createSchedule(valuesToNewSchedule(values, clientId, userId))
      showToast({ message: 'Schema aangemaakt. Toekomstige momenten worden gegenereerd.', tone: 'success' })
      setCreateOpen(false)
      await refresh()
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const edit = async (values: ScheduleFormValues) => {
    if (!editTarget || !clientId || saving) return
    setSaving(true)
    try {
      await repositories.medication.updateSchedule(editTarget.id, {
        name: values.name.trim(),
        healthType: values.healthType,
        dosage: values.dosage.trim(),
        quantity: values.quantity,
        instructions: values.instructions.trim() ? values.instructions.trim() : null,
        startDate: values.startDate,
        endDate: values.endDate ? values.endDate : null,
        localTime: values.localTime,
        timezone: values.timezone,
        recurrenceType: values.recurrenceType,
        recurrenceRule: recurrenceRuleFromValues(values),
      })
      showToast({ message: 'Schema bijgewerkt voor toekomstige momenten.', tone: 'success' })
      setEditTarget(null)
      await refresh()
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async () => {
    if (!deactivateTarget || saving) return
    setSaving(true)
    try {
      await repositories.medication.deactivateSchedule(deactivateTarget.id)
      showToast({ message: 'Schema gedeactiveerd. Bestaande geschiedenis blijft behouden.', tone: 'info' })
      setDeactivateTarget(null)
      await refresh()
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!clientId) {
    return (
      <EmptyState
        icon={<PillIcon size={26} />}
        title="Cliënt niet gevonden"
        action={
          <Button variant="secondary" onClick={() => navigate(PATHS.caregiver.clients)}>
            Terug naar cliënten
          </Button>
        }
      />
    )
  }

  return (
    <div className="caregiver-page">
      <button
        type="button"
        className="caregiver-back-link"
        onClick={() => navigate(PATHS.caregiver.clientDetail(clientId))}
      >
        <ChevronLeftIcon size={16} />
        Terug naar cliënt
      </button>

      <header>
        <h1 className="caregiver-section-title">Medicatieschema</h1>
        <p className="caregiver-muted">
          Beheer de medicatieplanning van deze cliënt. De cliënt ziet dit schema in de eigen app.
          Eerdere innames worden nooit aangepast.
        </p>
      </header>

      <Button variant="primary" onClick={() => setCreateOpen(true)}>
        <PlusIcon size={18} />
        Nieuw schema
      </Button>

      {status === 'loading' ? <Skeleton lines={3} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">Cloud niet ingesteld. Het schema kan nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {status === 'error' ? <ErrorState onRetry={() => void refresh()} /> : null}

      {status === 'ready' ? (
        <>
          {activeSchedules.length === 0 && inactiveSchedules.length === 0 ? (
            <EmptyState
              icon={<PillIcon size={26} />}
              title="Nog geen schema"
              body="Maak een eerste medicatieschema voor deze cliënt."
            />
          ) : (
            <>
              <section className="caregiver-section" aria-labelledby="cg-active-schedules">
                <h2 id="cg-active-schedules" className="caregiver-section-title">
                  Actieve schema’s
                </h2>
                {activeSchedules.length === 0 ? (
                  <p className="caregiver-muted">Geen actieve schema’s.</p>
                ) : (
                  <Card padding="sm">
                    <ul className="caregiver-list">
                      {activeSchedules.map((schedule) => (
                        <ScheduleRow
                          key={schedule.id}
                          schedule={schedule}
                          onEdit={() => setEditTarget(schedule)}
                          onDeactivate={() => setDeactivateTarget(schedule)}
                        />
                      ))}
                    </ul>
                  </Card>
                )}
              </section>
              {inactiveSchedules.length > 0 ? (
                <section className="caregiver-section" aria-labelledby="cg-inactive-schedules">
                  <h2 id="cg-inactive-schedules" className="caregiver-section-title">
                    Gedeactiveerd
                  </h2>
                  <Card padding="sm">
                    <ul className="caregiver-list">
                      {inactiveSchedules.map((schedule) => (
                        <li key={schedule.id} className="caregiver-event-item">
                          <span className="caregiver-event-text">
                            <span className="caregiver-event-title">{schedule.name}</span>
                            <span className="caregiver-event-meta">{describeSchedule(schedule)}</span>
                          </span>
                          <StatusChip status="neutral">Gedeactiveerd</StatusChip>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </section>
              ) : null}
            </>
          )}
        </>
      ) : null}

      <BottomSheet open={createOpen} onClose={() => setCreateOpen(false)} title="Nieuw medicatieschema">
        <ScheduleForm
          initialValues={emptyScheduleValues()}
          submitLabel="Schema aanmaken"
          saving={saving}
          onSubmit={(values) => void create(values)}
          onCancel={() => setCreateOpen(false)}
        />
      </BottomSheet>

      <BottomSheet open={editTarget !== null} onClose={() => setEditTarget(null)} title="Schema bewerken">
        {editTarget ? (
          <ScheduleForm
            initialValues={valuesFromSchedule(editTarget)}
            submitLabel="Wijzigingen opslaan"
            saving={saving}
            editMode
            onSubmit={(values) => void edit(values)}
            onCancel={() => setEditTarget(null)}
          />
        ) : null}
      </BottomSheet>

      <ConfirmDestructiveAction
        open={deactivateTarget !== null}
        title="Schema deactiveren"
        body="Er worden geen nieuwe medicatiemomenten meer gegenereerd. De geschiedenis van eerdere innames blijft ongewijzigd bewaard."
        confirmLabel="Deactiveren"
        onConfirm={() => void deactivate()}
        onCancel={() => setDeactivateTarget(null)}
      />
    </div>
  )
}

function ScheduleRow({
  schedule,
  onEdit,
  onDeactivate,
}: {
  schedule: MedicationSchedule
  onEdit: () => void
  onDeactivate: () => void
}) {
  return (
    <li className="caregiver-link-item">
      <div className="caregiver-link-header">
        <span className="caregiver-client-avatar" aria-hidden="true">
          <PillIcon size={20} />
        </span>
        <span className="caregiver-link-email">{schedule.name}</span>
        <StatusChip status="success">Actief</StatusChip>
      </div>
      <p className="caregiver-muted">{describeSchedule(schedule)}</p>
      {schedule.instructions ? <p className="caregiver-muted">{schedule.instructions}</p> : null}
      <div className="caregiver-actions">
        <Button variant="secondary" size="sm" onClick={onEdit}>
          Bewerken
        </Button>
        <Button variant="danger" size="sm" onClick={onDeactivate}>
          Deactiveren
        </Button>
      </div>
    </li>
  )
}
